import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { localAgentAPI } from '../../services/api';

export function LocalAgentConnection() {
  const { data, error, isPending } = useQuery({ queryKey: ['local-agent-status'], queryFn: localAgentAPI.status, refetchInterval: 15000 });
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [failure, setFailure] = useState('');
  return (
    <div className="border-b border-gray-200 dark:border-gray-700 px-3 py-2 text-xs text-gray-600 dark:text-gray-300">
      <button type="button" onClick={() => setExpanded(!expanded)} className="text-left">
        {isPending ? 'Checking local Claude connection…' : error ? 'Cannot reach your AI connection' : data?.online ? '● Local Claude connected · subscription' : '○ Local Claude offline · wake your paired Mac'}
      </button>
      {expanded && <div className="mt-2 space-y-2">
        <p>Chat uses Claude on your paired computer. Keep it awake and the Clazzo worker running.</p>
        <p>Local chat can manage tasks, documents, and habits. Scheduled routines and automatic summaries are not connected yet.</p>
        {data?.devices.map(device => <div key={device.id} className="flex items-center justify-between gap-2">
          <span>{device.name}</span>
          <button type="button" className="underline" onClick={async () => {
            try {
              await localAgentAPI.revoke(device.id);
              await queryClient.invalidateQueries({ queryKey: ['local-agent-status'] });
            } catch { setFailure('Could not disconnect this computer.'); }
          }}>Disconnect</button>
        </div>)}
        {!data?.devices.length && <p>Pair a computer by running the Clazzo worker’s pair command there.</p>}
        {failure && <p role="alert">{failure}</p>}
      </div>}
    </div>
  );
}

/** Pairing is an explicit authenticated browser action, never a GET side effect. */
export function LocalAgentPairing() {
  const { isAuthenticated } = useAuth();
  const [code, setCode] = useState(() => new URLSearchParams(window.location.search).get('pair_local_agent'));
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  if (!code || !isAuthenticated) return null;
  const close = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('pair_local_agent');
    window.history.replaceState({}, '', url);
    setCode(null);
  };
  return <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
    <section role="dialog" aria-modal="true" aria-labelledby="pair-title" className="max-w-md rounded-xl bg-white dark:bg-gray-800 p-6 text-gray-900 dark:text-white shadow-xl">
      <h2 id="pair-title" className="text-lg font-semibold">Connect your local AI computer</h2>
      <p className="mt-3 text-sm">Approve only if you just ran the Clazzo pairing command on your computer. It will be able to read your Clazzo context and handle your chat requests, including task edits.</p>
      <p className="my-3 font-mono text-sm">Pairing code: {code}</p>
      <p className="text-sm">Your Claude login stays on that computer. You can disconnect it from chat at any time.</p>
      {message && <p role="alert" className="mt-3 text-sm">{message}</p>}
      <div className="mt-5 flex gap-3">
        <button disabled={busy} className="rounded bg-blue-600 px-4 py-2 text-white disabled:opacity-50" onClick={async () => {
          setBusy(true);
          try {
            await localAgentAPI.approve(code);
            close();
          } catch { setMessage('Could not pair this computer. The code may have expired; run the pair command again.'); }
          finally { setBusy(false); }
        }}>Connect this computer</button>
        <button disabled={busy} onClick={close}>Cancel</button>
      </div>
    </section>
  </div>;
}
